import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  type DataAcquisitionState,
  type DataAcquisitionSortKey,
  type DataAcquisitionSortOrder,
  DATA_ACQUISITION_STATES,
  DATA_ACQUISITION_SORT_KEYS,
} from "@aeriosiq/types";

/** Sentinel value for the "Unassigned" pseudo-option in the Assignee
 * filter. Server-side: dev-api maps this to `assignedTo IS NULL`. */
export const ASSIGNEE_UNASSIGNED = "__unassigned__";

export interface DataAcquisitionsFilterState {
  q: string;
  // SB3-CC-G (lyse/v1@6.0.0): single-axis 8-value state filter per I5
  // supersedure. Covers both lifecycle values (CREATED, PROCESSING)
  // and analyst sub-workflow values (PRE_SCREENING, EXPERT_ANALYSIS,
  // EVALUATION_DONE, LOCKED). The previous separate `evaluation_state`
  // filter is retired; the field name `state` matches the wire param.
  state: DataAcquisitionState[];
  /** UUIDs of projects to include. Multi-select Autocomplete in the toolbar. */
  project_id: string[];
  /** UUIDs of sites (parks) to include. Multi-select Autocomplete in the toolbar. */
  site_id: string[];
  /** Assignee UUIDs OR the `ASSIGNEE_UNASSIGNED` sentinel. */
  assignee_id: string[];
  // SB6-DA-01 — "Show completed" toggle. Off by default; the BE then
  // hides the terminal states (LOCKED, REJECTED). URL: `includeCompleted=1`.
  includeCompleted: boolean;
  // FCII — link-status filter: All (default) / Linked / Unlinked. Server-side
  // via lyse/v1@8.3.0 `linkage` (parent-II project_id IS NULL). URL: `linkage`.
  linkage: "all" | "linked" | "unlinked";
  date_from: string;
  date_to: string;
  page: number;
  pageSize: number;
  /** Story 017 — single-column sort. `null` = use server default. */
  sortBy: DataAcquisitionSortKey | null;
  sortOrder: DataAcquisitionSortOrder | null;
}

export const DEFAULT_FILTERS: DataAcquisitionsFilterState = {
  q: "",
  state: [],
  project_id: [],
  site_id: [],
  assignee_id: [],
  includeCompleted: false,
  linkage: "all",
  date_from: "",
  date_to: "",
  page: 1,
  pageSize: 25,
  sortBy: null,
  sortOrder: null,
};

/**
 * Server-side default sort.
 *
 * R58-W-08 (R58-16) — flipped from `instruction_date desc` to
 * `instruction_date asc` on 2026-05-19. The pre-screener picks up
 * the oldest-captured dataset first so the highest-priority work
 * surfaces at the top.
 */
export const SORT_DEFAULT: {
  sortBy: DataAcquisitionSortKey;
  sortOrder: DataAcquisitionSortOrder;
} = { sortBy: "instruction_date", sortOrder: "asc" };

const DATA_ACQUISITION_STATE_SET = new Set<string>(DATA_ACQUISITION_STATES);
const SORT_KEY_SET = new Set<string>(DATA_ACQUISITION_SORT_KEYS);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUuid(v: string): boolean {
  return UUID_RE.test(v);
}

function isDataAcquisitionState(v: string): v is DataAcquisitionState {
  return DATA_ACQUISITION_STATE_SET.has(v);
}

function isSortKey(v: string): v is DataAcquisitionSortKey {
  return SORT_KEY_SET.has(v);
}

function isSortOrder(v: string): v is DataAcquisitionSortOrder {
  return v === "asc" || v === "desc";
}

function parseFromParams(sp: URLSearchParams): DataAcquisitionsFilterState {
  const qRaw = sp.get("q");
  const fromRaw = sp.get("date_from");
  const toRaw = sp.get("date_to");
  const pageRaw = sp.get("page");
  const pageSizeRaw = sp.get("pageSize");
  const sortByRaw = sp.get("sortBy");
  const sortOrderRaw = sp.get("sortOrder");

  // SB3-CC-G: read from `state` (the new unified param). Legacy
  // `evaluation_state` URL params from old deep-links are silently
  // dropped — they're not in the wipe list above so they get cleared
  // on the next setFilters call.
  const stateValues: DataAcquisitionState[] = [];
  for (const v of sp.getAll("state")) {
    if (isDataAcquisitionState(v)) stateValues.push(v);
  }

  const projectIdValues: string[] = [];
  for (const v of sp.getAll("project_id")) {
    if (isUuid(v)) projectIdValues.push(v);
  }
  const siteIdValues: string[] = [];
  for (const v of sp.getAll("site_id")) {
    if (isUuid(v)) siteIdValues.push(v);
  }

  const assigneeIdValues: string[] = [];
  for (const v of sp.getAll("assignee_id")) {
    if (isUuid(v) || v === ASSIGNEE_UNASSIGNED) assigneeIdValues.push(v);
  }

  // SB6-DA-01 — presence of `includeCompleted=1` flips the toggle on.
  const includeCompleted = sp.get("includeCompleted") === "1";

  // FCII — link-status: only the two engaging values survive; else "all".
  const linkageRaw = sp.get("linkage");
  const linkage: "all" | "linked" | "unlinked" =
    linkageRaw === "linked" || linkageRaw === "unlinked" ? linkageRaw : "all";

  let sortBy: DataAcquisitionSortKey | null = null;
  let sortOrder: DataAcquisitionSortOrder | null = null;
  if (sortByRaw && isSortKey(sortByRaw)) {
    sortBy = sortByRaw;
    sortOrder =
      sortOrderRaw && isSortOrder(sortOrderRaw) ? sortOrderRaw : "asc";
  } else if (sortByRaw) {
    console.warn(
      `[data-acquisitions] ignoring invalid sortBy="${sortByRaw}", falling back to default`,
    );
  }

  return {
    q: qRaw ?? "",
    state: stateValues,
    project_id: projectIdValues,
    site_id: siteIdValues,
    assignee_id: assigneeIdValues,
    includeCompleted,
    linkage,
    date_from: fromRaw ?? "",
    date_to: toRaw ?? "",
    page: pageRaw ? Math.max(1, Number(pageRaw)) : 1,
    pageSize: pageSizeRaw ? Math.max(1, Number(pageSizeRaw)) : 25,
    sortBy,
    sortOrder,
  };
}

function writeToParams(
  current: URLSearchParams,
  next: DataAcquisitionsFilterState,
): URLSearchParams {
  const out = new URLSearchParams(current);
  // Clear our keys, then re-write. `evaluation_state` and `inspection_type`
  // stay in the wipe list so stale deep-links don't survive setFilters;
  // both are retired by SB3-CC-G.
  for (const key of [
    "q",
    "state",
    "evaluation_state",
    "inspection_type",
    "project_id",
    "site_id",
    "assignee_id",
    "includeCompleted",
    "linkage",
    "date_from",
    "date_to",
    "page",
    "pageSize",
    "sortBy",
    "sortOrder",
  ]) {
    out.delete(key);
  }
  if (next.q) out.set("q", next.q);
  for (const s of next.state) out.append("state", s);
  for (const id of next.project_id) out.append("project_id", id);
  for (const id of next.site_id) out.append("site_id", id);
  for (const id of next.assignee_id) out.append("assignee_id", id);
  // SB6-DA-01 — only serialise when on; absence = default (terminal hidden).
  if (next.includeCompleted) out.set("includeCompleted", "1");
  // FCII — only serialise the two engaging values; absence = default `all`.
  if (next.linkage !== "all") out.set("linkage", next.linkage);
  if (next.date_from) out.set("date_from", next.date_from);
  if (next.date_to) out.set("date_to", next.date_to);
  if (next.page !== 1) out.set("page", String(next.page));
  if (next.pageSize !== 25) out.set("pageSize", String(next.pageSize));
  if (next.sortBy && next.sortOrder) {
    out.set("sortBy", next.sortBy);
    out.set("sortOrder", next.sortOrder);
  }
  return out;
}

export interface UseDataAcquisitionsFiltersResult {
  filters: DataAcquisitionsFilterState;
  setFilters: (next: Partial<DataAcquisitionsFilterState>) => void;
  clear: () => void;
  hasActiveFilters: boolean;
}

export function useDataAcquisitionsFilters(): UseDataAcquisitionsFiltersResult {
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => parseFromParams(sp), [sp]);

  const setFilters = useCallback(
    (next: Partial<DataAcquisitionsFilterState>) => {
      setSp((prev) => {
        const current = parseFromParams(prev);
        const merged: DataAcquisitionsFilterState = { ...current, ...next };
        if (next.page === undefined) merged.page = 1;
        return writeToParams(prev, merged);
      });
    },
    [setSp],
  );

  const clear = useCallback(() => {
    setSp((prev) => writeToParams(prev, DEFAULT_FILTERS));
  }, [setSp]);

  const hasActiveFilters =
    filters.q.length > 0 ||
    filters.state.length > 0 ||
    filters.project_id.length > 0 ||
    filters.site_id.length > 0 ||
    filters.assignee_id.length > 0 ||
    // SB6-DA-01 — the toggle counts as active only when on (it is off by
    // default), so "Clear filters" resets it without it lighting up on load.
    filters.includeCompleted ||
    filters.linkage !== "all" ||
    filters.date_from.length > 0 ||
    filters.date_to.length > 0;

  return { filters, setFilters, clear, hasActiveFilters };
}
